import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon paths under bundlers
const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// SVG-based blue marker for user position
const userIcon = L.divIcon({
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid white;box-shadow:0 0 0 2px #2563eb55, 0 4px 8px rgba(0,0,0,.3)"></div>`,
  className: '',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const SetView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom || map.getZoom(), { animate: true });
  }, [center, zoom, map]);
  return null;
};

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick?.(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

/**
 * LocationMap component
 * Props:
 *   lat, lng: current marker position (nullable)
 *   onPick: (lat, lng) => void — fires on map click (picker mode)
 *   stores: optional array of {id, name, lat, lng, distance_km, ...}
 *   userPosition: optional {lat, lng}
 *   height: e.g. '300px'
 *   readonly: boolean — no click handler
 */
export const LocationMap = ({
  lat,
  lng,
  onPick,
  stores = [],
  userPosition,
  height = '320px',
  readonly = false,
  testId = 'location-map',
  zoom: zoomProp,
  markerText,
}) => {
  const defaultCenter = [lat || userPosition?.lat || -15.7942, lng || userPosition?.lng || -47.8822]; // Brasil
  const zoom = zoomProp ?? (((lat && lng) || userPosition) ? 13 : 4);

  return (
    <div
      className="rounded-xl overflow-hidden border border-slate-200 shadow-sm"
      style={{ height }}
      data-testid={testId}
    >
      <MapContainer center={defaultCenter} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {!readonly && onPick && <ClickHandler onPick={onPick} />}
        {(lat != null && lng != null) && (
          <Marker position={[lat, lng]}>
            <Popup>{markerText || 'Posição selecionada'}</Popup>
          </Marker>
        )}
        {userPosition && (
          <Marker position={[userPosition.lat, userPosition.lng]} icon={userIcon}>
            <Popup>Você está aqui</Popup>
          </Marker>
        )}
        {stores.map((s) => (
          s.lat && s.lng ? (
            <Marker key={s.id} position={[s.lat, s.lng]}>
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">{s.name}</p>
                  {s.city && <p className="text-slate-500">{s.city} / {s.state}</p>}
                  {s.distance_km != null && (
                    <p className="text-blue-600 font-medium mt-1">{s.distance_km} km de você</p>
                  )}
                  <a
                    href={`/loja/${s.id}`}
                    className="block mt-2 text-blue-600 underline"
                  >Ver loja →</a>
                </div>
              </Popup>
            </Marker>
          ) : null
        ))}
        <SetView
          center={(lat && lng) ? [lat, lng] : userPosition ? [userPosition.lat, userPosition.lng] : null}
          zoom={zoom}
        />
      </MapContainer>
    </div>
  );
};

export default LocationMap;
